/** @format */

import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
	Users,
	MapPin,
	Zap,
	CheckCircle,
	Award,
	Phone,
	ArrowRight,
	Truck,
} from "lucide-react"; // Added/Refined Icons
import ReactMarkdown from "react-markdown";

// --- Reusable Section Component ---
const Section = ({ children, className = "", bg = "bg-transparent" }) => (
	<section className={`py-16 md:py-20 lg:py-24 ${bg} ${className}`}>
		{" "}
		{/* Adjusted padding */}
		<div className="container mx-auto px-4">{children}</div>
	</section>
);

// --- Reusable Image Section Component (Revised for flexibility) ---
const ImageSection = ({
	image,
	imgAlt,
	title,
	subtitle, // Optional subtitle
	children,
	reverse = false,
	imgClass = "aspect-square", // Default aspect ratio
	textClass = "lg:w-1/2", // Default text width
	imgContainerClass = "lg:w-1/2", // Default image width
}) => (
	<div
		className={`flex flex-col ${
			reverse ? "lg:flex-row-reverse" : "lg:flex-row"
		} gap-10 md:gap-16 items-center`}
	>
		{/* Image Column */}
		<motion.div
			initial={{ opacity: 0, x: reverse ? 40 : -40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.3 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			className={`w-full ${imgContainerClass}`}
		>
			<img
				src={image}
				alt={imgAlt || title || "Off Cut Shades"} // Better alt text
				className={`w-full h-auto object-cover rounded-xl shadow-lg ${imgClass}`} // Use h-auto
				loading="lazy" // Add lazy loading
			/>
		</motion.div>
		{/* Text Column */}
		<motion.div
			initial={{ opacity: 0, x: reverse ? -40 : 40 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, amount: 0.3 }}
			transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
			className={`w-full ${textClass} space-y-4`}
		>
			{subtitle && (
				<p className="text-sm font-semibold text-primary uppercase tracking-wider">
					{subtitle}
				</p>
			)}
			{title && (
				<h2 className="text-3xl md:text-4xl font-bold text-gray-900 !mt-1">
					{title}
				</h2>
			)}
			<div className="text-base md:text-lg text-gray-600 space-y-4">
				{children}
			</div>
		</motion.div>
	</div>
);

// --- Reusable Contact Banner (Simplified, more direct CTA) ---
const ContactBanner = () => (
	<div className="relative rounded-xl overflow-hidden bg-secondary text-white p-8 md:p-12 text-center">
		{/* Subtle background elements if desired */}
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.5 }}
			transition={{ duration: 0.5 }}
			className="relative z-10"
		>
			<h2 className="text-3xl font-semibold mb-3">
				Have Questions or Need Help?
			</h2>
			<p className="text-white/80 mb-6 max-w-xl mx-auto">
				Our expert team is ready to assist with measurements, fabric choices, or
				anything else you need.
			</p>
			<Link
				to="/contact"
				className="inline-flex items-center gap-2 px-6 py-3 bg-white text-secondary rounded-lg font-semibold hover:bg-gray-100 transition-colors text-base shadow-sm"
			>
				<Phone className="w-5 h-5" /> Contact Our Experts
			</Link>
		</motion.div>
	</div>
);

// --- Main About Us Screen ---
export default function AboutUsScreen() {
	return (
		<>
			<Helmet>
				<title>
					About Off Cut Shades | Canadian Made, Simple Custom Blinds
				</title>{" "}
				{/* SEO Title */}
				<meta
					name="description"
					content="Learn about Off Cut Shades, your Toronto-based experts for high-quality, custom-made window shades. Discover our commitment to simplicity, speed, and local Canadian craftsmanship."
				/>{" "}
				{/* SEO Desc */}
				<link rel="canonical" href="https://offcutshades.com/about" />
				{/* Removed Breadcrumb schema for brevity */}
			</Helmet>

			<main className="bg-gray-50">
				{" "}
				{/* Consistent background */}
				{/* --- Hero Section --- */}
				<Section
					bg="bg-gradient-to-b from-white via-gray-50 to-gray-100"
					className="pt-24 md:pt-32 text-center"
				>
					{" "}
					{/* Added bg gradient */}
					<motion.div
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, ease: "easeOut" }}
						className="max-w-3xl mx-auto"
					>
						{/* Optional Icon */}
						{/* <div className="inline-block bg-primary/10 p-3 rounded-full mb-4"><Home size={24} className="text-primary"/></div> */}
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight"
						>
							Custom Shades, <span className="text-primary">Made Simple.</span>
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-lg md:text-xl text-gray-600"
						>
							Discover high-quality, Canadian-made window treatments designed
							for effortless style and delivered with surprising speed.
						</motion.p>
					</motion.div>
				</Section>
				{/* --- Our Mission / The Problem We Solve --- */}
				<Section bg="bg-white">
					<ImageSection
						image="/images/about/about-us.jpg" // Choose relevant image (e.g., person looking frustrated at samples vs happy person)
						imgAlt="Simplifying the custom blind shopping experience"
						title="Tired of Complicated Customization?"
						subtitle="Our Mission"
						textClass="lg:w-1/2"
						imgContainerClass="lg:w-1/2"
					>
						<p>
							Shopping for custom window treatments can feel overwhelming –
							endless choices, confusing measurements, long waits, and uncertain
							quality.
						</p>
						<p>
							At Off Cut Shades, we believe getting beautiful, perfectly fitting
							shades shouldn't be stressful. Our mission is to{" "}
							<strong className="font-semibold text-gray-800">
								simplify the entire process
							</strong>
							, offering expertly crafted products with clear options and
							exceptional speed, all made right here in Canada.
						</p>
						<Link
							to="/shop"
							className="inline-flex items-center gap-2 text-primary font-medium hover:underline mt-4 text-base"
						>
							Explore Our Shades <ArrowRight size={16} />
						</Link>
					</ImageSection>
				</Section>
				{/* --- Our Approach / The Off Cut Difference --- */}
				<Section bg="bg-gray-50">
					{" "}
					{/* Alternate background */}
					<ImageSection
						image="/images/about/design.jpg" // Image of factory, precise cutting, or streamlined process
						imgAlt="Efficient custom shade manufacturing process"
						title="Quality & Speed You Can Trust"
						subtitle="The Off Cut Difference"
						reverse={true} // Alternate layout
						textClass="lg:w-1/2"
						imgContainerClass="lg:w-1/2"
					>
						<p>
							We combine decades of window covering expertise with modern
							efficiency right in our Toronto facility. This allows us to offer:
						</p>
						{/* Feature List */}
						<ul className="space-y-3 !mt-5">
							<li className="flex items-start gap-3">
								<div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
									{" "}
									<CheckCircle size={16} className="text-primary" />{" "}
								</div>
								<div>
									{" "}
									<strong className="font-semibold text-gray-800">
										Simplified Choices:
									</strong>{" "}
									Shop our curated "Easy Pick" options designed by experts, or
									dive deep with full customization – the choice is yours.{" "}
								</div>
							</li>
							<li className="flex items-start gap-3">
								<div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
									{" "}
									<Zap size={16} className="text-primary" />{" "}
								</div>
								<div>
									{" "}
									<strong className="font-semibold text-gray-800">
										Unbeatable Speed:
									</strong>{" "}
									Our efficient local production means your custom shades are
									typically ready to ship in just{" "}
									<strong className="font-semibold text-gray-800">
										2 business days
									</strong>
									.{" "}
								</div>
							</li>
							<li className="flex items-start gap-3">
								<div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
									{" "}
									<Award size={16} className="text-primary" />{" "}
								</div>
								<div>
									{" "}
									<strong className="font-semibold text-gray-800">
										Premium Quality:
									</strong>{" "}
									We use top-grade materials and components, ensuring your
									shades look beautiful and operate smoothly for years.{" "}
								</div>
							</li>
						</ul>
					</ImageSection>
				</Section>
				{/* --- Proudly Canadian Made Section --- */}
				<Section bg="bg-white">
					<ImageSection
						image="/images/about/local.jpg" // Image showing team, Toronto skyline, or Canadian element
						imgAlt="Off Cut Shades proudly Canadian made in Toronto"
						title="Proudly Canadian, Expertly Crafted"
						subtitle="Local Commitment"
						textClass="lg:w-1/2"
						imgContainerClass="lg:w-1/2"
					>
						<p>
							From design to final assembly, your shades are meticulously
							crafted by our skilled team in Toronto. Choosing Off Cut Shades
							means investing in quality you can trust while supporting local
							Canadian manufacturing.
						</p>
						<p>
							This local focus allows us unparalleled quality control and
							enables our industry-leading production speed.
						</p>
						{/* Icons representing benefits */}
						<div className="grid grid-cols-2 gap-4 !mt-6 text-sm">
							<div className="flex items-center gap-2 text-gray-700">
								<MapPin size={18} className="text-primary flex-shrink-0" />{" "}
								Toronto-Based Manufacturing
							</div>
							<div className="flex items-center gap-2 text-gray-700">
								<Users size={18} className="text-primary flex-shrink-0" />{" "}
								Skilled Local Craftspeople
							</div>
							<div className="flex items-center gap-2 text-gray-700">
								<CheckCircle size={18} className="text-primary flex-shrink-0" />{" "}
								Rigorous Quality Control
							</div>
							<div className="flex items-center gap-2 text-gray-700">
								<Truck size={18} className="text-primary flex-shrink-0" />{" "}
								Faster North American Shipping
							</div>
						</div>
					</ImageSection>
				</Section>
				{/* --- Contact Banner --- */}
				<Section bg="bg-gray-100">
					{" "}
					{/* Consistent background */}
					<ContactBanner />
				</Section>
			</main>
		</>
	);
}
